import React from 'react';
import Icon from '../../../components/AppIcon';

const SetupProgress = ({ connections }) => {
  const connectedCount = Object.values(connections).filter(conn => conn.connected).length;
  const totalCount = Object.keys(connections).length;
  const progressPercentage = (connectedCount / totalCount) * 100;

  const getProgressStatus = () => {
    if (connectedCount === 0) {
      return {
        title: 'Get Started',
        description: 'Connect your first music streaming account to begin syncing playlists.',
        color: 'text-text-muted',
        bgColor: 'bg-surface-secondary'
      };
    } else if (connectedCount === totalCount) {
      return {
        title: 'Setup Complete!',
        description: 'All accounts connected. You\'re ready to start syncing playlists between platforms.',
        color: 'text-success',
        bgColor: 'bg-success'
      };
    } else {
      return {
        title: 'Almost There',
        description: `${connectedCount} of ${totalCount} accounts connected. Connect remaining accounts for full sync capabilities.`,
        color: 'text-warning',
        bgColor: 'bg-warning'
      };
    }
  };

  const status = getProgressStatus();

  return (
    <div className="bg-surface border border-border rounded-xl p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`flex items-center justify-center w-10 h-10 ${status.bgColor}/10 rounded-lg`}>
            <Icon 
              name={connectedCount === totalCount ? "CheckCircle" : "Settings"} 
              size={20} 
              className={status.color}
            />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{status.title}</h3>
            <p className="text-sm text-text-secondary">Account Setup Progress</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-bold text-text-primary">{connectedCount}/{totalCount}</div>
          <div className="text-xs text-text-muted">Connected</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-text-secondary">Setup Progress</span>
          <span className={`font-medium ${status.color}`}>{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-surface-secondary rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ease-out ${status.bgColor}`}
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Status Description */}
      <p className="text-sm text-text-secondary mb-4">{status.description}</p>

      {/* Connected Accounts List */}
      <div className="space-y-2">
        {Object.entries(connections).map(([platform, connection]) => (
          <div key={platform} className="flex items-center justify-between p-3 bg-background rounded-lg border border-border">
            <div className="flex items-center space-x-3">
              <Icon 
                name={platform === 'spotify' ? 'Music' : 'Play'} 
                size={16} 
                className={connection.connected ? 
                  (platform === 'spotify' ? 'text-success' : 'text-error') : 
                  'text-text-muted'
                }
              />
              <span className="text-sm font-medium text-text-primary capitalize">{platform}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              {connection.connected ? (
                <>
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-xs text-success font-medium">Connected</span>
                </>
              ) : (
                <>
                  <Icon name="Circle" size={16} className="text-text-muted" />
                  <span className="text-xs text-text-muted">Not Connected</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Next Steps */}
      {connectedCount === totalCount && (
        <div className="mt-4 p-4 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="ArrowRight" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Ready for Next Step</span>
          </div>
          <p className="text-xs text-text-secondary">
            Visit the Dashboard to start syncing your playlists between platforms.
          </p>
        </div>
      )}
    </div>
  );
};

export default SetupProgress;